import { useState } from 'react';
import './gallery.scss';

export interface GalleryImage {
  src: string;
  alt?: string;
  caption?: string;
  grayscale?: boolean;
}

interface GalleryProps {
  images: GalleryImage[];
}

function GalleryFigure({ image }: { image: GalleryImage }) {
  const fallbackAlt = image.alt ?? image.caption ?? '';
  const className = image.grayscale ? 'gallery-item grayscale' : 'gallery-item';

  return (
    <figure className={className}>
      <div className="gallery-item__media">
        <img src={image.src} alt="" aria-hidden="true" className="gallery-img-backdrop" />
        <img src={image.src} alt={fallbackAlt} />
      </div>
      {image.caption && (
        <figcaption className="gallery-item__caption">
          {image.caption}
        </figcaption>
      )}
    </figure>
  );
}

export function Gallery({ images }: GalleryProps) {
  const count = images.length;
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev' | null>(null);

  if (count === 0) return null;

  const nextIndex = (current + 1) % count;
  const prevIndex = (current - 1 + count) % count;

  const go = (newIndex: number, dir: 'next' | 'prev') => {
    setDirection(dir);
    setCurrent(newIndex);
  };

  const slotClass = direction ? ` gallery-slot--from-${direction}` : '';

  if (count === 1) {
    return (
      <div className="gallery gallery--single">
        <div className="gallery-single-inner">
          <div className="gallery-slot">
            <GalleryFigure image={images[0]} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery">
      <div className="gallery-gutter" />
      <div className="gallery-main">
        <div className={`gallery-slot${slotClass}`} key={current}>
          <GalleryFigure image={images[current]} />
        </div>
        {count > 1 && (
          <>
            <button
              className="gallery-arrow gallery-arrow--prev"
              onClick={() => go(prevIndex, 'prev')}
              aria-label="Previous"
            >
              <svg width="18" height="28" viewBox="0 0 18 28" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M16.7279 26.6274C15.1658 28.1895 12.6332 28.1895 11.0711 26.6274L1.17157 16.7279C-0.390524 15.1658 -0.390524 12.6332 1.17157 11.0711C2.73367 9.50897 5.26633 9.50897 6.82843 11.0711L16.7279 20.9706C18.29 22.5327 18.29 25.0653 16.7279 26.6274Z" fill="var(--fill-low)"/>
<path d="M1.17157 16.7279C-0.390524 15.1658 -0.390524 12.6332 1.17157 11.0711L11.0711 1.17158C12.6332 -0.390519 15.1658 -0.390519 16.7279 1.17158C18.29 2.73367 18.29 5.26633 16.7279 6.82843L6.82843 16.7279C5.26634 18.29 2.73367 18.29 1.17157 16.7279Z" fill="var(--fill-low)"/>
</svg>

            </button>
            <button
              className="gallery-arrow gallery-arrow--next"
              onClick={() => go(nextIndex, 'next')}
              aria-label="Next"
            >
              <svg width="18" height="28" viewBox="0 0 18 28" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.17155 1.17157C2.73365 -0.390525 5.26631 -0.390525 6.82841 1.17157L16.7279 11.0711C18.29 12.6332 18.29 15.1658 16.7279 16.7279C15.1658 18.29 12.6331 18.29 11.071 16.7279L1.17155 6.82843C-0.390545 5.26633 -0.390545 2.73367 1.17155 1.17157Z" fill="var(--fill-low)"/>
<path d="M16.7279 11.0711C18.29 12.6332 18.29 15.1658 16.7279 16.7279L6.8284 26.6274C5.2663 28.1895 2.73364 28.1895 1.17155 26.6274C-0.390551 25.0653 -0.390551 22.5326 1.17155 20.9705L11.071 11.0711C12.6331 9.50896 15.1658 9.50897 16.7279 11.0711Z" fill="var(--fill-low)"/>
</svg>

            </button>
          </>
        )}
      </div>
      {count > 1 && (
        <div className="gallery-peek" aria-hidden="true">
          <div className="gallery-slot gallery-slot--peek-inner">
            <GalleryFigure image={images[nextIndex]} />
          </div>
        </div>
      )}
    </div>
  );
}